<template>
  <Teleport to="body">
    <div v-if="modelValue" class="fixed inset-0 z-[70] overflow-y-auto">
      <div class="fixed inset-0 bg-black/50" @click="close"></div>
      <div class="relative flex min-h-screen items-center justify-center p-4">
        <div class="relative flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
          <header class="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-4">
            <div>
              <h3 class="text-lg font-semibold text-slate-900">{{ modalTitle }}</h3>
              <p class="mt-1 text-sm text-slate-500">{{ mode === 'copy' ? '复制已有方案到目标月份。' : '创建新的企营增月度方案。' }}</p>
            </div>
            <button
              type="button"
              class="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              @click="close"
            >
              <span class="sr-only">关闭</span>
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </header>

          <main class="min-h-0 flex-1 overflow-y-auto px-6 py-5">
            <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
              <label v-if="mode === 'copy'" class="block md:col-span-2">
                <span class="form-label">复制来源 <span class="text-red-500">*</span></span>
                <input
                  :value="basePlanLabel"
                  readonly
                  class="form-input mt-1 w-full bg-slate-50"
                >
              </label>

              <label class="block md:col-span-2">
                <span class="form-label">方案名称 <span class="text-red-500">*</span></span>
                <input
                  v-model="form.planName"
                  type="text"
                  class="form-input mt-1 w-full"
                  placeholder="请输入方案名称"
                >
              </label>

              <label class="block">
                <span class="form-label">方案月份 <span class="text-red-500">*</span></span>
                <input
                  v-model="form.planMonth"
                  type="month"
                  class="form-input mt-1 w-full"
                >
              </label>

              <template v-if="mode === 'create'">
                <label class="block">
                  <span class="form-label">额度偏差（%）</span>
                  <input
                    v-model="form.amountToleranceRatePercent"
                    type="number"
                    step="0.01"
                    min="0"
                    class="form-input mt-1 w-full"
                  >
                </label>

                <label class="block">
                  <span class="form-label">利润率偏差（百分点）</span>
                  <input
                    v-model="form.profitRateTolerancePointsPercent"
                    type="number"
                    step="0.01"
                    min="0"
                    class="form-input mt-1 w-full"
                  >
                </label>

                <label class="block">
                  <span class="form-label">最少主体数</span>
                  <input
                    v-model.number="form.minChainSubjectCount"
                    type="number"
                    min="10"
                    class="form-input mt-1 w-full"
                  >
                </label>

                <label class="block">
                  <span class="form-label">最多主体数</span>
                  <input
                    v-model.number="form.maxChainSubjectCount"
                    type="number"
                    min="10"
                    class="form-input mt-1 w-full"
                  >
                </label>

                <label class="block">
                  <span class="form-label">金额步长</span>
                  <input
                    v-model.number="form.amountStep"
                    type="number"
                    step="100"
                    class="form-input mt-1 w-full"
                  >
                </label>

                <label class="block md:col-span-2">
                  <span class="form-label">起始金额档位 <span class="text-red-500">*</span></span>
                  <input
                    v-model="form.startAmountTiersText"
                    type="text"
                    class="form-input mt-1 w-full"
                    placeholder="200000,300000,500000"
                  >
                  <p class="mt-1 text-xs text-slate-500">多个档位用英文逗号分隔，金额按 100 元取整。</p>
                </label>
              </template>
            </div>
          </main>

          <footer class="flex justify-end gap-3 border-t border-slate-200 px-6 py-4">
            <button type="button" class="btn-secondary" :disabled="submitting" @click="close">取消</button>
            <button type="button" class="btn-primary" :disabled="submitting" @click="submit">
              {{ submitting ? '保存中...' : '保存' }}
            </button>
          </footer>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { createEnterpriseInvoicePlan, copyEnterpriseInvoicePlan } from '@/api/invoice'
import { parsePercentInput } from '../utils'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  mode: { type: String, default: 'create' },
  basePlan: { type: Object, default: null }
})

const emit = defineEmits(['update:modelValue', 'saved'])

const form = reactive(defaultForm())
const submitting = ref(false)
const parsedStartAmountTiers = ref([])
const parsedAmountToleranceRate = ref(null)
const parsedProfitRateTolerancePoints = ref(null)

const modalTitle = computed(() => props.mode === 'copy' ? '复制月度方案' : '新建月度方案')
const basePlanLabel = computed(() => {
  if (!props.basePlan) return '-'
  return `${props.basePlan.planMonth || '-'} ${props.basePlan.planName || ''}`.trim()
})

function defaultForm() {
  return {
    basePlanId: null,
    planName: '',
    planMonth: '',
    amountToleranceRatePercent: '5.00',
    profitRateTolerancePointsPercent: '0.50',
    minChainSubjectCount: 10,
    maxChainSubjectCount: 20,
    amountStep: 100,
    startAmountTiersText: '200000,300000,500000,800000,1000000'
  }
}

function resetForm() {
  Object.assign(form, defaultForm())
  parsedStartAmountTiers.value = []
  parsedAmountToleranceRate.value = null
  parsedProfitRateTolerancePoints.value = null
  if (props.mode === 'copy' && props.basePlan) {
    form.basePlanId = props.basePlan.id
    form.planName = `${props.basePlan.planName || ''} 副本`.trim()
  }
}

function close() {
  emit('update:modelValue', false)
}

function parseStartAmountTiers() {
  const parts = form.startAmountTiersText.split(',').map(item => item.trim()).filter(Boolean)
  const amounts = parts.map(item => Number(item))
  if (parts.length === 0 || amounts.some(item => !Number.isFinite(item) || item <= 0)) {
    return null
  }
  if (amounts.some(item => item % 100 !== 0)) {
    return 'INVALID_STEP'
  }
  return amounts
}

function validate() {
  parsedStartAmountTiers.value = []
  parsedAmountToleranceRate.value = null
  parsedProfitRateTolerancePoints.value = null
  if (!form.planName?.trim()) {
    ElMessage.warning('请填写方案名称')
    return false
  }
  if (!form.planMonth) {
    ElMessage.warning('请选择方案月份')
    return false
  }
  if (props.mode === 'copy') {
    if (!form.basePlanId) {
      ElMessage.warning('请选择要复制的方案')
      return false
    }
    return true
  }
  const amountToleranceRate = parsePercentInput(form.amountToleranceRatePercent)
  if (!Number.isFinite(amountToleranceRate) || amountToleranceRate < 0) {
    ElMessage.warning('请填写有效的额度偏差')
    return false
  }
  parsedAmountToleranceRate.value = amountToleranceRate
  const profitRateTolerancePoints = parsePercentInput(form.profitRateTolerancePointsPercent)
  if (!Number.isFinite(profitRateTolerancePoints) || profitRateTolerancePoints < 0) {
    ElMessage.warning('请填写有效的利润率偏差')
    return false
  }
  parsedProfitRateTolerancePoints.value = profitRateTolerancePoints
  const minCount = Number(form.minChainSubjectCount)
  const maxCount = Number(form.maxChainSubjectCount)
  if (minCount < 10) {
    ElMessage.warning('链路主体数最少为 10')
    return false
  }
  if (maxCount < minCount) {
    ElMessage.warning('最大主体数不能小于最小主体数')
    return false
  }
  if (Number(form.amountStep) !== 100) {
    ElMessage.warning('第一版金额步长固定为 100 元')
    return false
  }
  const startAmountTiers = parseStartAmountTiers()
  if (startAmountTiers === 'INVALID_STEP') {
    ElMessage.warning('起始金额档位必须是 100 元整数倍')
    return false
  }
  if (!startAmountTiers) {
    ElMessage.warning('请填写起始金额档位')
    return false
  }
  parsedStartAmountTiers.value = startAmountTiers
  return true
}

function createPayload() {
  return {
    planName: form.planName.trim(),
    planMonth: form.planMonth,
    amountToleranceRate: parsedAmountToleranceRate.value,
    profitRateTolerancePoints: parsedProfitRateTolerancePoints.value,
    minChainSubjectCount: Number(form.minChainSubjectCount),
    maxChainSubjectCount: Number(form.maxChainSubjectCount),
    amountStep: Number(form.amountStep),
    startAmountTiers: parsedStartAmountTiers.value
  }
}

function copyPayload() {
  return {
    sourcePlanId: form.basePlanId,
    planName: form.planName.trim(),
    planMonth: form.planMonth
  }
}

async function submit() {
  if (submitting.value || !validate()) return
  submitting.value = true
  try {
    const plan = props.mode === 'copy'
      ? await copyEnterpriseInvoicePlan(copyPayload())
      : await createEnterpriseInvoicePlan(createPayload())
    ElMessage.success(props.mode === 'copy' ? '方案已复制' : '方案已创建')
    emit('saved', plan)
    close()
  } catch (error) {
    console.error('Failed to save enterprise invoice plan', error)
    ElMessage.error(error.message || '保存方案失败')
  } finally {
    submitting.value = false
  }
}

watch(() => props.modelValue, value => {
  if (value) resetForm()
})

watch(() => props.basePlan, () => {
  if (props.modelValue) resetForm()
})
</script>
